import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { DateTime } from 'luxon';
import { getCurrentBusiness } from '../utils/OrgManagement.ts';
import {
  TransactionAmountComparatorEnum,
  TransactionFlowFilterEnum,
  TransactionMatchFilterEnum,
} from "../types/enums.ts";
import type {BillTodoType, DateFilterType, DocumentCategory, DocumentDirection, FilterTags, ToCheckReason} from '../generated/enums.ts';

//IMPORTANT !!: If you change the schema, you must increment the SCHEMA_VERSION
const SCHEMA_VERSION = 8;
const REMOVED_DOCUMENT_TAGS = ['possible_duplicate'];
const LOCAL_STORAGE_KEY_PREFIX = 'filterStore';

const getStorageScope = (): string => {
  try {
    return getCurrentBusiness() || 'global';
  } catch {
    return 'global';
  }
};

const getStorageKey = (): string => `${LOCAL_STORAGE_KEY_PREFIX}:${getStorageScope()}`;

export type SerializedDateRange = {
  from: string;
  to: string;
};

export type FilterState = {
  version: number;
  common: {
    customerId: string | null;
    dateRange: SerializedDateRange;
  };

  documents: {
    dateType: DateFilterType;
    relations: string[];
    documentDirection: DocumentDirection | 'all';
    documentCategories: DocumentCategory[];
    tags: string[];
    tagsMode: FilterTags;
  };

  documentsToCheck: {
    dateType: DateFilterType;
    reasons: ToCheckReason[];
    todoTypes: BillTodoType[];
  };

  transactions: {
    flow: TransactionFlowFilterEnum;
    amountComparator: TransactionAmountComparatorEnum | null;
    amount: number | null;
    bankAccountId: string | null;
    relationIban: string | null;
    generalLedgerAccountId: string | null;
    matchStatus: TransactionMatchFilterEnum;
    search: string;
  };
};

type StoredFilterState = Partial<FilterState>;

export type Action =
  | {
    type: 'SET_FILTERS';
    payload: {
      common?: Partial<FilterState['common']>;
      documents?: Partial<FilterState['documents']>;
      documentsToCheck?: Partial<FilterState['documentsToCheck']>;
      transactions?: Partial<FilterState['transactions']>;
    }
  }
  | { type: 'RESET' }
  | { type: 'RESET_DOCUMENT_FILTERS' }
  | { type: 'RESET_DOCUMENTS_TO_CHECK_FILTERS' }
  | { type: 'RESET_TRANSACTION_FILTERS' };

const getStoredFilterState = (): string | null => {
  try {
    return localStorage.getItem(getStorageKey());
  } catch {
    return null;
  }
};

const setStoredFilterState = (value: string): void => {
  try {
    localStorage.setItem(getStorageKey(), value);
  } catch (e) {
    console.error('Failed to save filter state to localStorage:', e);
  }
};

const clearStoredFilterState = (): void => {
  try {
    localStorage.removeItem(getStorageKey());
  } catch (e) {
    console.error('Failed to clear persisted filter state', e);
  }
};

export const getInitialDateRange = (): SerializedDateRange => {
  const now = DateTime.now();

  return {
    from: now.startOf('quarter').toISODate()!,
    to: now.endOf('quarter').toISODate()!,
  };
};

const getDefaultState = (): FilterState => ({
  version: SCHEMA_VERSION,
  common: {
    customerId: null,
    dateRange: getInitialDateRange(),
  },
  documents: {
    dateType: 'invoice',
    relations: [],
    documentDirection: 'all',
    documentCategories: [],
    tags: [],
    tagsMode: 'is',
  },
  documentsToCheck: {
    dateType: 'created',
    reasons: [],
    todoTypes: [],
  },
  transactions: {
    flow: TransactionFlowFilterEnum.ANY,
    amountComparator: TransactionAmountComparatorEnum.GT,
    amount: null,
    bankAccountId: null,
    relationIban: null,
    generalLedgerAccountId: null,
    matchStatus: TransactionMatchFilterEnum.ANY,
    search: '',
  },
});

const getResetDocumentFiltersState = (state: FilterState): FilterState => {
  const defaultState = getDefaultState();
  return {
    ...state,
    documents: defaultState.documents,
  };
};

const getResetDocumentsToCheckFiltersState = (state: FilterState): FilterState => {
  const defaultState = getDefaultState();
  return {
    ...state,
    common: { ...defaultState.common, customerId: state.common.customerId },
    documentsToCheck: defaultState.documentsToCheck,
  };
};

const getResetTransactionFiltersState = (state: FilterState): FilterState => {
  const defaultState = getDefaultState();
  return {
    ...state,
    transactions: defaultState.transactions,
  };
};

const parseAndValidateStoredFilter = (raw: string): StoredFilterState | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const stored = parsed as StoredFilterState;
  if (stored?.version !== SCHEMA_VERSION) {
    return null;
  }
  return stored;
};

const getInitialState = (): FilterState => {
  const defaultState = getDefaultState();
  const raw = getStoredFilterState();
  if (!raw) return defaultState;

  const storedState = parseAndValidateStoredFilter(raw);
  if (!storedState) {
    clearStoredFilterState();
    return defaultState;
  }

  return {
    ...defaultState,
    ...storedState,
    common: { ...defaultState.common, ...storedState.common },
    documents: {
      ...defaultState.documents,
      ...storedState.documents,
      tags: (storedState.documents?.tags ?? []).filter((tag) => !REMOVED_DOCUMENT_TAGS.includes(tag)),
    },
    documentsToCheck: { ...defaultState.documentsToCheck, ...storedState.documentsToCheck },
    transactions: { ...defaultState.transactions, ...storedState.transactions },
  };
};

function reducer(state: FilterState, action: Action): FilterState {
  switch (action.type) {
    case 'SET_FILTERS':
      return {
        ...state,
        common: action.payload.common ? { ...state.common, ...action.payload.common } : state.common,
        documents: action.payload.documents ? { ...state.documents, ...action.payload.documents } : state.documents,
        documentsToCheck: action.payload.documentsToCheck ? { ...state.documentsToCheck, ...action.payload.documentsToCheck } : state.documentsToCheck,
        transactions: action.payload.transactions ? { ...state.transactions, ...action.payload.transactions } : state.transactions,
      };
    case 'RESET':
      return getDefaultState();

    case 'RESET_DOCUMENT_FILTERS':
      return getResetDocumentFiltersState(state);

    case 'RESET_DOCUMENTS_TO_CHECK_FILTERS':
      return getResetDocumentsToCheckFiltersState(state);

    case 'RESET_TRANSACTION_FILTERS':
      return getResetTransactionFiltersState(state);

    default:
      return state;
  }
}

const FilterStoreContext = createContext<{
  state: FilterState;
  dispatch: React.Dispatch<Action>;
} | null>(null);


export const FilterStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState);

  useEffect(() => {
    setStoredFilterState(JSON.stringify(state));
  }, [state]);

  return (
    <FilterStoreContext.Provider value={{ state, dispatch }}>
      {children}
    </FilterStoreContext.Provider>
  );
};

export const useFilterStore = () => {
  const context = useContext(FilterStoreContext);
  if (!context) {
    throw new Error('useFilterStore must be used within FilterStoreProvider');
  }
  return context;
};
